import { SaveOperation } from '@infuse/api/common';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export function useGenericDeactivateOrganisation({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<never, never>;
  forcedParentId?: string;
}) {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, string>({
    mutationFn: async (organisationId: string) => {
      try {
        if (forcedParentId) {
          await apiFn({ parentId: forcedParentId, id: organisationId }, undefined as never);
        } else {
          await apiFn({ id: organisationId }, undefined as never);
        }
        notify({
          title: t('iam.organisations.header.deactivate_organisation'),
          message: t('iam.organisations.notify.deactivate_organisation_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.organisations.header.deactivate_organisation'),
          message: getAxiosErrorMessage(e, t('iam.organisations.notify.deactivate_organisation_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisations'] });
    }
  });

  return {
    ...mutation
  };
}
